import {
  AddressType,
  checkShippingLabels,
  OrderDataType,
} from '@/utils/dpdApi';
import i18n from '@/utils/i18n';
import {
  pickpackTypesByVytalType,
  VytalStoreTypes,
} from '../../views/MerchantComponents/StoreTypeSelector';
import {
  DeliveryAddress,
  MerchantOrderItem,
  MerchantOrderItemStatus,
  MerchantOrderType,
} from './HandleStatusChange';

export interface SlackUrlObject {
  Id: number;
  SlackUrl: string;
}

export interface ContactInfo {
  email: string;
  phone: string;
  first_name: string;
  last_name: string;
}

export function isContainerCountAllowedForOnboarding(
  merchantOrderTypeId: number,
  items: MerchantOrderItem[],
  storeType: string,
  newItemTypeId = 0,
  newAmount = 0,
) {
  const limits = pickpackTypesByVytalType[VytalStoreTypes.Canteen].includes(
    storeType,
  )
    ? [100, 200, 200, 999999]
    : [30, 40, 45, 60];
  const orderAmount = items
    .filter(
      (i) =>
        i.typeId > 0 &&
        i.status === MerchantOrderItemStatus.Delivery &&
        i.typeId !== newItemTypeId,
    )
    .reduce((acc, cur) => acc + parseInt(cur.amount as string), 0);
  if (
    merchantOrderTypeId === MerchantOrderType.OnboardingS &&
    orderAmount + newAmount > limits[0]
  ) {
    alert(
      i18n.t('MerchantOrder:alertOnboardingTooManyBowls', {
        type: 'S',
        amount: limits[0],
      }),
    );
    return false;
  } else if (
    merchantOrderTypeId === MerchantOrderType.OnboardingM &&
    orderAmount + newAmount > limits[1]
  ) {
    alert(
      i18n.t('MerchantOrder:alertOnboardingTooManyBowls', {
        type: 'M',
        amount: limits[1],
      }),
    );
    return false;
  } else if (
    merchantOrderTypeId === MerchantOrderType.Onboarding &&
    orderAmount + newAmount > limits[2]
  ) {
    alert(
      i18n.t('MerchantOrder:alertOnboardingTooManyBowls', {
        type: 'M',
        amount: limits[2],
      }),
    );
    return false;
  } else if (
    merchantOrderTypeId === MerchantOrderType.OnboardingL &&
    orderAmount + newAmount > limits[3]
  ) {
    alert(
      i18n.t('MerchantOrder:alertOnboardingTooManyBowls', {
        type: 'L',
        amount: limits[3],
      }),
    );
    return false;
  }
  return true;
}

export function getItemIndex(
  status: number,
  typeId: number,
  items: MerchantOrderItem[],
) {
  return items.findIndex((i) => i.status === status && i.typeId === typeId);
}

export function getDeliveryAddress(deliveryAddress: any, country?: string) {
  // Gate the state on the delivery address's own country (same source the
  // state dropdown uses), falling back to the passed-in country. Using the
  // store's country here would strip a valid state when shipping cross-border
  // to a US/CA address, which DPD then rejects with CLOUD_STATE_STATESHORT.
  const effectiveCountry = (deliveryAddress.country || country || '').trim();
  const isUSA = effectiveCountry === 'US';
  const isCanada = effectiveCountry === 'CA';

  return {
    company: (deliveryAddress.company || '').trim(),
    streetname: (deliveryAddress.streetname || '').trim(),
    streetno: (deliveryAddress.streetno || '').trim(),
    zip: (deliveryAddress.zip || '').trim(),
    city: (deliveryAddress.city || '').trim(),
    country: (deliveryAddress.country || country || 'de' || '').trim(),
    firstName: (deliveryAddress.first_name || '').trim(),
    lastName: (deliveryAddress.last_name || '').trim(),
    additionalInfo: (deliveryAddress.additional_info || '').trim(),
    email: (deliveryAddress.email || '').trim(),
    phoneNumber: (deliveryAddress.phone_number || '').trim(),
    state: isUSA || isCanada ? (deliveryAddress.state || '').trim() : '',
  };
}

export async function checkDeliveryAddress(
  address: AddressType,
  storeTextId: string,
  orderExecutionDate: Date,
): Promise<boolean> {
  // Allow order creation without house number by skipping DPD pre-check
  if (address.houseNo == null || address.houseNo.trim().length === 0) {
    return true;
  }
  const payload: OrderDataType = {
    parcelShopID: 0,
    parcelData: {
      reference1: `${(storeTextId || 'str_store_id').substring(4)} 0`,
      content: 'test',
      weight: 0,
      yourInternalID: 1,
      shipService: 'Classic_Predict',
    },
    shipAddress: address,
  };
  const errors = await checkShippingLabels([payload], orderExecutionDate);
  for (const error of errors) {
    alert(`${error.errorCode}:  ${error.errorMsgLong}`);
  }
  return errors.length === 0;
}

export function deliveryAddressToDpdAddress(address: DeliveryAddress) {
  let name = '';
  if (
    address.firstName != null &&
    address.firstName.trim().length > 0 &&
    address.lastName != null &&
    address.lastName.trim().length > 0
  ) {
    name = `${address.firstName} ${address.lastName}`;
  } else if (address.firstName != null && address.firstName.trim().length > 0) {
    name = address.firstName;
  } else if (address.lastName != null && address.lastName.trim().length > 0) {
    name = address.lastName;
  }

  const res: AddressType = {
    company: address.company,
    salutation: '',
    firstName: address.firstName,
    lastName: address.lastName,
    name,
    street: address.streetname,
    houseNo: address.streetno,
    zipCode: address.zip,
    city: address.city,
    country: address.country,
    state: address.state || '',
    phone: address.phoneNumber,
    mail: address.email,
  };
  return res;
}

export function resolveDeliveryAddress(store: any) {
  const combined: any = {
    company: store.name,
    country: store.country_code || 'DE', // use as default
  };

  // make sure default values are not overridden with null values
  for (const key of Object.keys(store.delivery_address || {})) {
    if (store.delivery_address[key] != null) {
      combined[key] = truncateAddressFields(store.delivery_address[key], key);
    }
  }
  return combined;
}

/**
 * Builds a single OrderDataType for DPD API from revision and store information
 * @param revision - The merchant order revision
 * @param deliveryAddress - The delivery address to use
 * @param storeId - Store identifier (can be GUID or textId)
 * @param deliveryNoteId - Delivery note ID (for reference1/content)
 * @param packageIndex - Current package index (1-based)
 * @param packageCount - Total number of packages
 * @param weight - Package weight (string, will be parsed)
 * @param yourInternalID - Internal ID for DPD mapping (default: 0)
 * @param shipService - Ship service type (default: 'Classic_Predict', or 'Shop_Return' for sample orders)
 * @returns OrderDataType for DPD API
 */
export function buildDpdOrderDataItem(
  revision: {
    type: string | number;
    deliveryAddress?: any;
  },
  deliveryAddress: DeliveryAddress,
  storeId: string,
  deliveryNoteId: string | number,
  packageIndex: number,
  packageCount: number,
  weight: string,
  yourInternalID: number = 0,
  shipService: string = 'Classic_Predict',
): OrderDataType {
  // Build reference1 and content strings, ensuring they're between 1-35 characters
  // DPD requires these fields to be 1-35 characters
  const reference1 = `${storeId} ${deliveryNoteId}`.substring(0, 35);
  const content = `${storeId} ${deliveryNoteId}`.substring(0, 35);

  return {
    parcelShopID: 0,
    shipAddress: deliveryAddressToDpdAddress(deliveryAddress),
    parcelData: {
      reference1: reference1,
      reference2: `${packageIndex}/${packageCount}`,
      content: content,
      weight: parseInt(weight || '1', 10),
      yourInternalID: yourInternalID,
      shipService: shipService,
    },
  };
}

export function truncateAddressFields(value: string, nameIn: string) {
  let truncatedValue = value;
  const name = nameIn.startsWith('delivery_address.')
    ? nameIn.substring(17)
    : nameIn;
  if (name === 'company') {
    truncatedValue = (value || '').substring(0, 35);
  }
  if (name === 'first_name') {
    truncatedValue = (value || '').substring(0, 25);
  }
  if (name === 'last_name') {
    truncatedValue = (value || '').substring(0, 25);
  }
  if (name === 'streetname') {
    truncatedValue = (value || '').substring(0, 50);
  }
  if (name === 'streetno') {
    truncatedValue = (value || '').substring(0, 8);
  }
  if (name === 'zip') {
    truncatedValue = (value || '').substring(0, 10);
  }
  if (name === 'city') {
    truncatedValue = (value || '').substring(0, 50);
  }
  if (name === 'phone_number') {
    truncatedValue = (value || '')
      // allow digits, spaces, +, -, (, ) only
      .replace(/[^0-9\s+\-()]/g, '')
      .substring(0, 20);
  }
  if (name === 'email') {
    truncatedValue = (value || '').substring(0, 50);
  }
  return truncatedValue;
}
